import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShieldCheck, Check } from "lucide-react";

const PolicyFilterDrawer = ({ open, onClose, onApply, initial }) => {
  const [status, setStatus] = React.useState(
    initial?.status || { active: true, inactive: false }
  );
  const [policyType, setPolicyType] = React.useState(
    initial?.policyType || "all"
  );
  const [routeType, setRouteType] = React.useState(initial?.routeType || "all");
  const [duration, setDuration] = React.useState(initial?.duration || "all");
  const panelRef = useRef(null);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const apply = () => onApply?.({ status, policyType, routeType, duration });

  const clearFilters = () => {
    setStatus({ active: true, inactive: false });
    setPolicyType("all");
    setRouteType("all");
    setDuration("all");
    onApply?.({
      status: { active: true, inactive: false },
      policyType: "all",
      routeType: "all",
      duration: "all",
    });
  };

  const insuranceTypes = [
    { value: "all", label: "All Insurance Types" },
    { value: "tripsecure_insurance", label: "TripSecure Insurance" },
    { value: "travel_insurance", label: "Travel Insurance" },
    { value: "life_insurance", label: "Life Insurance" },
    { value: "general_insurance", label: "General Insurance" },
  ];
  
  const policyTypes = ["all", "intercity_travel", "okada_rider", "daily_commuter"];

  const routeTypes = ["all", "regional", "international"];

  const durations = [
    "all",
    "4 hours",
    "6 hours",
    "8 hours",
    "12 hours",
    "15 hours",
    "20 hours",
    "24 hours",
    "30 days",
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            ref={panelRef}
            initial={{ x: -420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -420, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed left-0 top-0 z-[70] h-full w-[90%] max-w-md overflow-hidden rounded-r-2xl bg-white shadow-2xl ring-1 ring-gray-100"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />

            <div className="flex justify-between items-start p-4 sm:p-5">
              <div className="flex gap-3 items-center">
                <div className="grid place-items-center w-10 h-10 text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Policies filter
                  </p>
                  <p className="text-xs text-gray-500">
                    Refine by policy type, route, duration and status
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="grid place-items-center w-9 h-9 text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="w-full h-px bg-gray-100" />

            <div className="space-y-5 p-4 sm:p-5 overflow-y-auto h-[calc(100%-5rem)]">
              {/* Insurance Types */}
              <div>
                <p className="text-xs font-semibold text-gray-700">
                  Insurance Type
                </p>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {insuranceTypes.map(({ value, label }) => {
                    const selected = value === policyType;
                    return (
                      <button
                        key={value}
                        onClick={() => setPolicyType(value)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          selected
                            ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{label}</span>
                        {selected && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Policy Types */}
              <div>
                <p className="text-xs font-semibold text-gray-700">
                  Policy Type
                </p>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {policyTypes.map((type) => {
                    const selected = type === policyType;
                    const displayName =
                      type === "all"
                        ? "All Types"
                        : type === "intercity_travel"
                        ? "Intercity Travel"
                        : type === "okada_rider"
                        ? "Motor Rider"
                        : type === "daily_commuter"
                        ? "Daily Commuter"
                        : type;
                    return (
                      <button
                        key={type}
                        onClick={() => setPolicyType(type)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          selected
                            ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{displayName}</span>
                        {selected && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Route Types */}
              <div>
                <p className="text-xs font-semibold text-gray-700">
                  Route Type
                </p>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {routeTypes.map((route) => {
                    const selected = route === routeType;
                    const displayName =
                      route === "all"
                        ? "All Routes"
                        : route === "regional"
                        ? "Regional"
                        : route === "international"
                        ? "International"
                        : route;
                    return (
                      <button
                        key={route}
                        onClick={() => setRouteType(route)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          selected
                            ? "text-blue-700 bg-blue-50 border-blue-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{displayName}</span>
                        {selected && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Coverage Duration */}
              <div>
                <p className="text-xs font-semibold text-gray-700">
                  Coverage Duration
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {durations.map((dur) => {
                    const selected = dur === duration;
                    return (
                      <button
                        key={dur}
                        onClick={() => setDuration(dur)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          selected
                            ? "text-orange-700 bg-orange-50 border-orange-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">
                          {dur === "all" ? "All Durations" : dur}
                        </span>
                        {selected && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div>
                <p className="text-xs font-semibold text-gray-700">Status</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    { key: "active", label: "Active" },
                    { key: "inactive", label: "Inactive" },
                  ].map(({ key, label }) => {
                    const checked = status[key];
                    return (
                      <button
                        key={key}
                        onClick={() =>
                          setStatus((s) => ({ ...s, [key]: !s[key] }))
                        }
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          checked
                            ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span>{label}</span>
                        {checked && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 rounded-xl ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Clear Filters
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 rounded-xl ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={apply}
                  className="px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-700 rounded-xl shadow-md hover:from-emerald-700 hover:to-green-800"
                >
                  Apply
                </button>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default PolicyFilterDrawer;
